import apiV2 from '../api/v2';

export type DmNotificationSort = 'level' | 'latest' | 'waiting';
export type DmNotificationSortOrder = 'asc' | 'desc';
// Closed: nothing open in the inbox and something resolved in the last 24 hours.
export type DmInboxStatus = 'open' | 'closed';
// Backend-enforced ceiling on `search` for the notifications and messages inboxes.
export const DM_INBOX_SEARCH_MAX_LENGTH = 200;
// V1 also matches patients with no version.
export type DmPatientVersion = 'V1' | 'V2';

export interface DmNotificationFinding {
    title: string | null;
    notificationReference: string | null;
    patientInstructions: string[];
    teamInstructions: string[];
}

export interface DmNotification {
    id: string;
    dmEventId: string;
    eventType: string;
    level: string;
    title: string | null;
    content: string | null;
    createdAt: string;
    dmScanId: string | null;
    finding: DmNotificationFinding | null;
    // Set on a closed patient's resolved items; null in open mode, absent from older servers.
    resolvedAt?: string | null;
    resolution?: DmNotificationResolution | null;
    resolvedInDm?: boolean | null;
    resolvedBy?: DmNotificationReviewer | null;
}

export interface DmNotificationAssignee {
    userId: string;
    fullname: string | null;
    email: string;
}

export type DmNotificationReviewer = DmNotificationAssignee;

export type DmNotificationResolution = 'dashboard' | 'dm' | 'gone' | 'excluded';

export interface DmResolvedNotification extends DmNotification {
    resolvedAt: string;
    resolution: DmNotificationResolution;
    resolvedInDm: boolean;
    resolvedBy: DmNotificationReviewer | null;
}

// The patient's latest item closed in the last 24 hours, in either mode.
export interface DmLastResolution {
    resolution: DmNotificationResolution;
    resolvedAt: string;
    resolvedByName: string | null;
    resolvedInDm: boolean;
}

// Carried by both inboxes' rows and patient headers.
export interface DmInboxPatientFields {
    currentAligner: number | null;
    totalAligners: number | null;
    monitoringStatus: string | null;
    lastResolution: DmLastResolution | null;
}

export interface DmNotificationLevelCounts {
    alert: number;
    warning: number;
    info: number;
    other: number;
}

export interface DmNotificationPatientRow extends DmInboxPatientFields {
    patientId: string;
    patientName: string;
    practiceId: string | null;
    practiceName: string | null;
    doctorName: string | null;
    profileThumbnailUrl: string | null;
    version: string | null;
    openCount: number;
    // Both null only on the patient view, for a patient with nothing open.
    latestAt: string | null;
    oldestAt: string | null;
    highestLevel: string;
    levelCounts: DmNotificationLevelCounts;
    assignee: DmNotificationAssignee | null;
    // Newest first, as sent by the server.
    notifications: DmNotification[];
}

export interface DmNotificationsMeta {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    totalNotifications: number;
}

export interface DmNotificationsPage {
    data: DmNotificationPatientRow[];
    meta: DmNotificationsMeta;
}

export interface DmNotificationFilters {
    sort: DmNotificationSort;
    order: DmNotificationSortOrder;
    search?: string;
    practiceIds?: string[];
    levels?: string[];
    // Emails, plus 'null' for unassigned; matched with OR.
    assignees?: string[];
    versions?: DmPatientVersion[];
    status?: DmInboxStatus;
}

export interface DmNotificationsParams extends DmNotificationFilters {
    page: number;
    limit: number;
}

function filterQuery(filters: DmNotificationFilters): Record<string, string | string[]> {
    const query: Record<string, string | string[]> = {
        sort: filters.sort,
        order: filters.order
    };
    if (filters.search?.trim()) query.search = filters.search.trim();
    if (filters.practiceIds?.length) query.practiceIds = filters.practiceIds;
    if (filters.levels?.length) query.levels = filters.levels;
    if (filters.assignees?.length) query.assignees = filters.assignees;
    if (filters.versions?.length) query.versions = filters.versions;
    return { ...query, ...statusQuery(filters.status ?? 'open') };
}

// Open is the server's default, so it's left off.
export function statusQuery(status: DmInboxStatus): Record<string, string> {
    return status === 'closed' ? { status } : {};
}

export const fetchDmNotifications = async (
    params: DmNotificationsParams
): Promise<DmNotificationsPage> => {
    const query = {
        ...filterQuery(params),
        page: String(params.page),
        limit: String(params.limit)
    };
    const response = await apiV2.get<{ success: boolean } & DmNotificationsPage>(
        '/admin/monitoring/notifications',
        { params: query }
    );
    return { data: response.data.data, meta: response.data.meta };
};

// position is 1-based in the list's order, null once the patient no longer matches its filters.
export interface DmListPosition {
    position: number | null;
    total: number;
}

export interface DmNotificationPatientView {
    // Every open notification, unfiltered; may be empty.
    patient: DmNotificationPatientRow;
    // Neighbours within the list's filters; null at either end or once the patient drops out.
    previousPatientId: string | null;
    nextPatientId: string | null;
    // Resolved in the last 24 hours, newest first; unfiltered like `patient`.
    recentlyResolved: DmResolvedNotification[];
    // Older servers omit it.
    listPosition?: DmListPosition;
}

// Older servers omit recentlyResolved.
type DmNotificationPatientViewResponse = Omit<DmNotificationPatientView, 'recentlyResolved'> & {
    recentlyResolved?: DmResolvedNotification[];
};

export const fetchDmNotificationPatient = async (
    patientId: string,
    filters: DmNotificationFilters
): Promise<DmNotificationPatientView> => {
    const response = await apiV2.get<{
        success: boolean;
        data: DmNotificationPatientViewResponse;
    }>(`/admin/monitoring/notifications/patients/${encodeURIComponent(patientId)}`, {
        params: filterQuery(filters)
    });
    const view = response.data.data;
    return { ...view, recentlyResolved: view.recentlyResolved ?? [] };
};

export const updateDmNotificationAssignee = async (
    patientId: string,
    email: string | null
): Promise<DmNotificationAssignee | null> => {
    const response = await apiV2.put<{
        success: boolean;
        data: { assignee: DmNotificationAssignee | null };
    }>(`/admin/monitoring/notifications/patients/${encodeURIComponent(patientId)}/assignee`, {
        email
    });
    return response.data.data.assignee;
};

export interface DmNotificationReviewResult {
    reviewed: true;
    reviewedInDm: boolean;
    alreadyResolvedInDm: boolean;
}

export const reviewDmNotification = async (
    notificationId: string,
    sendActivation?: boolean
): Promise<DmNotificationReviewResult> => {
    const response = await apiV2.post<{ success: boolean; data: DmNotificationReviewResult }>(
        `/admin/monitoring/notifications/${encodeURIComponent(notificationId)}/review`,
        sendActivation === undefined ? undefined : { sendActivation }
    );
    return response.data.data;
};

export interface DmNotificationCount {
    total: number;
    patients: number;
}
